import {
    DefaultStorageKeys,
    SessionStorageManager,
    StorageManager,
} from '@speeki/storage-manager';

import { MfaMetadata } from '@@types/CommonTypes';

import { sentry } from '@services/Sentry';

import { client } from '@boot/apolloClient';
import {
    authVar,
    logoutProcessingVar,
    resetVars,
} from '@boot/reactiveVariables';
import { REPORT_CHAT_SIZE_STORAGE_KEY } from '@domains/Chat/ChatElement/reportChatStorage';

interface LogoutOptions {
    keepTenantData: boolean;
}

const getToken = (tokenName: keyof DefaultStorageKeys) => {
    try {
        const token = StorageManager.getValue(tokenName);
        const isLoggedIn = authVar().isLoggedIn;

        if (!token && isLoggedIn) throw new Error(`Cannot get ${tokenName}`);
        return token;
    } catch (error) {
        console.error(`getToken : ${tokenName} ::`, error);
    }
};

export class AuthService {
    static setSessionToken = (token: string) =>
        StorageManager.setValue('graphSessionToken', token);
    static setRefreshToken = (token: string) =>
        StorageManager.setValue('graphRefreshToken', token);
    static setMfaMetadata = (metadata: MfaMetadata) =>
        StorageManager.setValue('metadata', JSON.stringify(metadata));
    static setTenantUuid = (uuid: string) =>
        StorageManager.setValue('tenantUuid', uuid);

    static getSessionToken = () => getToken('graphSessionToken');
    static getRefreshToken = () => getToken('graphRefreshToken');
    static getMfaMetadata = (): MfaMetadata | undefined => {
        const metadata = StorageManager.getValue('metadata');

        if (metadata) return JSON.parse(metadata);
    };
    static getTenantUuid = () => StorageManager.getValue('tenantUuid');
    static getGraphApi = () => StorageManager.getValue('graphApi');

    static logout = async (
        redirectAfterLogout: boolean,
        options?: LogoutOptions,
    ) => {
        try {
            logoutProcessingVar(true);

            resetVars();

            StorageManager.removeValue('oaToken');
            StorageManager.removeValue('graphRefreshToken');
            StorageManager.removeValue('graphSessionToken');
            StorageManager.removeValue('metadata');
            StorageManager.removeValue('excludedSurvey');
            SessionStorageManager.removeValue('subsidiarySite');
            /**
             * The reporter's chat size is already session-only; clearing it
             * on logout means the next person at a shared computer does not
             * inherit it even within the same tab. Written raw by the chat
             * package, so cleared raw — and never allowed to break logout.
             */
            try {
                window.sessionStorage.removeItem(REPORT_CHAT_SIZE_STORAGE_KEY);
            } catch {
                /** Blocked storage has nothing to clear. */
            }

            if (!options?.keepTenantData) {
                StorageManager.removeValue('graphApi');
                StorageManager.removeValue('tenantUuid');
            }

            await client.clearStore();

            sentry.clearScope();

            if (redirectAfterLogout) {
                window.location.href = `${window.location.origin}/auth/welcome`;
            }
        } catch (error) {
            console.error('AuthService : logout ::', error);
        } finally {
            logoutProcessingVar(false);
        }
    };
}
